import { useAppDispatch } from '../../../../../redux/hooks';
import { playGame } from '../../../../../redux/slices/gameParrotSlice';
import { getOccButtons, numToCoords } from '../../gameUtils';
import useBattleshipModalStyles from '../useBattleshipModalStyles';

/**
 * Interface representing the props for the Battleship Button component.
 *
 * - idx The number of the button to help determine its coordinates on the board.
 * - selfId The user ID of the user whose board this button is a part of.
 * - oppId The user ID of the user whose board this button is not a part of.
 * - gameStatus Array containing information for every turn taken in the game.
 * - canPlay Boolean representing whether or not the user can take their turn.
 * - type The type of button, either one on the board displaying your own ships or one
 *   one the board where you can make guesses on the locations of an opponent's ships.
 * - hits Array containing information for all hits that have been made during the game.
 */
interface BattleshipButtonProps {
  idx: number;
  selfId: string;
  oppId: string;
  gameStatus: (string | number | number[][])[][];
  canPlay: boolean;
  type: string;
  hits: number[][];
}

/**
 * Battleship Button component renders a button, many of which make up a battleship game board.
 *
 * - @param idx The number of the button to help determine its coordinates on the board.
 * - @param selfId The user ID of the user whose board this button is a part of.
 * - @param oppId The user ID of the user whose board this button is not a part of.
 * - @param gameStatus Array containing information for every turn taken in the game.
 * - @param canPlay Boolean representing whether or not the user can take their turn.
 * - @param type The type of button, either one on the board displaying your own ships or one
 *   @param one the board where you can make guesses on the locations of an opponent's ships.
 * - @param hits Array containing information for all hits that have been made during the game.
 */
const BattleshipButton = ({
  idx,
  selfId,
  oppId,
  gameStatus,
  canPlay,
  type,
  hits,
}: BattleshipButtonProps) => {
  const { classes } = useBattleshipModalStyles();
  const dispatch = useAppDispatch();

  /**
   * Function to handle the clicking of a tic tac toe button which notifies the opponent
   * of the move that the user has made.
   */
  const handleClick = () => {
    if (canPlay && !getOccButtons(selfId, gameStatus, 10).includes(idx) && type === 'opponent')
      dispatch(
        playGame({
          going: selfId,
          waiting: oppId,
          data: numToCoords(idx, 10),
          type: 'turn',
        }),
      );
  };

  const [buttonCoordX, buttonCoordY] = numToCoords(idx, 10);
  const boatCoords = (gameStatus.length && (gameStatus[0][4] as number[][])) || [];

  /**
   * Function to get the type of boat that this button represents, if any. Each type is a number
   * which represents the size of the boat.
   *
   * @returns the type of boat that this button represents.
   */
  const getBoatType = () => {
    for (const coords of boatCoords) {
      if (buttonCoordX === coords[1] && buttonCoordY === coords[2]) return coords[0];
    }
    return -1;
  };

  /**
   * Function to get the class of the boat section that this button represents, if any. These classes
   * are based on whether or not the section of the boat represented by the button is on the end or
   * in the middle, and whether or not the full boat is oriented horizontally or vertically.
   *
   * @param coords - The coordinates of the boat section on the game board.
   * @param boatInfo - Array containing the information for all of the user's boats.
   * @returns the class of the boat section represented by this buttons.
   */
  const getBoatClass = (coords: number[], boatInfo: number[][]) => {
    const [id, coordsX, coordsY] = coords;
    if (id === -1) return '';
    const hasAdjacent = (xAdd: number, yAdd: number) =>
      boatInfo.some(
        boat => boat[0] === id && boat[1] === coordsX + xAdd && boat[2] === coordsY + yAdd,
      );
    if (!hasAdjacent(0, -1) && !hasAdjacent(0, 1) && !hasAdjacent(1, 0) && !hasAdjacent(-1, 0))
      return classes.oneBoat;
    if (!hasAdjacent(0, -1) && hasAdjacent(0, 1)) return classes.topBoat;
    if (hasAdjacent(0, -1) && !hasAdjacent(0, 1)) return classes.bottomBoat;
    if (hasAdjacent(0, -1) && hasAdjacent(0, 1)) return classes.middleVertBoat;
    if (!hasAdjacent(1, 0) && hasAdjacent(-1, 0)) return classes.leftEndBoat;
    if (hasAdjacent(1, 0) && !hasAdjacent(-1, 0)) return classes.rightEndBoat;
    if (hasAdjacent(1, 0) && hasAdjacent(-1, 0)) return classes.middleHorBoat;
    return '';
  };

  let hitId = -1;
  hits.forEach(hit => {
    const [id, coordX, coordY] = hit;
    if (buttonCoordX === coordX && buttonCoordY === coordY) hitId = id;
  });

  /**
   * Function that returns a boolean representing whether or not the boat that this button is
   * a part of should be fully revealed, which returns yes if all boat sections have been hit,
   * and no otherwise.
   *
   * @returns the boolean representing whether or not this boat should be revealed.
   */
  const revealBoat = () => {
    if (hitId === -1) return false;
    const idHits = hits.filter(hit => hit[0] === hitId);
    const noDups: number[][] = [];
    idHits.forEach(hit => {
      if (!noDups.some(i => hit[1] === i[1] && hit[2] === i[2])) noDups.push(hit);
    });
    if (noDups.length === hitId + 1) return true;
    return false;
  };

  const selfBoatClass = getBoatClass([getBoatType(), buttonCoordX, buttonCoordY], boatCoords);
  const hitBoatClass = getBoatClass([hitId, buttonCoordX, buttonCoordY], hits);

  /**
   * Function to get the overall class of this button. This differs from the class of the boat
   * section in that the button can represent a hit, missed, or not yet occupied location on
   * the game board.
   *
   * @returns the overall class of this button.
   */
  const getButtonClass = () => {
    if (getOccButtons(oppId, gameStatus, 10).includes(idx) && type === 'self')
      return selfBoatClass ? `${selfBoatClass} ${classes.hitByOpp}` : classes.miss;
    if (type === 'self') return selfBoatClass || classes.noBoat;
    if (getOccButtons(selfId, gameStatus, 10).includes(idx) && type === 'opponent' && hitId > -1)
      return revealBoat() ? `${hitBoatClass} ${classes.revealBoat}` : classes.hitBySelf;
    if (getOccButtons(selfId, gameStatus, 10).includes(idx) && type === 'opponent' && hitId === -1)
      return classes.miss;
    if (!getOccButtons(selfId, gameStatus, 10).includes(idx) && type === 'opponent')
      return classes.unOcc;
    return '';
  };

  return <button className={`${classes.button} ${getButtonClass()}`} onClick={handleClick} />;
};

export default BattleshipButton;
